import React from "react";
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import moment from "moment";
import CircularProgress from "@mui/material/CircularProgress";

import ImportErrorList from "../../ImportErrorList";

function ColumnMappingStep({ logic }) {
  // Opções de campos para o mapeamento
  const fieldOptions = [
    { value: "", label: "Ignorar coluna" },
    { value: "nome", label: "Nome do Produto" },
    { value: "quantidade", label: "Quantidade" },
    { value: "quantidadeIdeal", label: "Stock mínimo" },
    { value: "lote", label: "Lote" },
    { value: "dataValidade", label: "Data de Validade" },
    { value: "unidadeMedida", label: "Unidade de Medida" },
    { value: "precoCompra", label: "Preço de Compra" },
    { value: "fornecedor", label: "Fornecedor" },
    // Novos campos
    { value: "classificacao", label: "Classificação" },
  ];

  // Verificar se os campos obrigatórios estão mapeados
  const requiredFieldsMapped =
    Object.values(logic.columnMapping).includes("nome") &&
    Object.values(logic.columnMapping).includes("quantidade");

  // Obter amostra de dados para previsualização
  const previewRows = (logic.originalData || []).slice(0, 5);

  return (
    <div className="enhanced-import-step column-mapping-step">
      <div className="column-mapping-header">
        <h3>Mapeamento de Colunas</h3>
        <p>
          Associe cada coluna do seu arquivo aos campos corretos do sistema.
          <Tooltip title="Os campos Nome do Produto e Quantidade são obrigatórios para importação.">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </p>

        <FormControlLabel
          control={
            <Checkbox
              checked={logic.hasHeaderRow}
              onChange={logic.toggleHeaderRow}
              color="primary"
            />
          }
          label="Arquivo contém linha de cabeçalho"
          className="column-mapping-header-checkbox"
        />
      </div>

      {!requiredFieldsMapped && (
        <Alert severity="warning" className="column-mapping-warning">
          <strong>Atenção:</strong> Os campos Nome do Produto e Quantidade são
          obrigatórios para prosseguir.
        </Alert>
      )}

      <div className="column-mapping-container">
        {/* Tabela de mapeamento de colunas */}
        <div className="column-mapping-table">
          <div className="column-mapping-table-header">
            <div className="column-mapping-table-col">Coluna no Arquivo</div>
            <div className="column-mapping-table-col">Campo no Sistema</div>
            <div className="column-mapping-table-col">Prévia</div>
          </div>

          <div className="column-mapping-table-body">
            {logic.detectedColumns.map((column, index) => (
              <div className="column-mapping-table-row" key={index}>
                <div className="column-mapping-table-col">
                  {logic.hasHeaderRow
                    ? column.original || `Coluna ${index + 1}`
                    : `Coluna ${index + 1}`}
                </div>
                <div className="column-mapping-table-col">
                  <select
                    value={logic.columnMapping[index] || ""}
                    onChange={(e) =>
                      logic.updateColumnMapping(index, e.target.value)
                    }
                    className="column-mapping-select"
                  >
                    {fieldOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="column-mapping-table-col column-mapping-preview">
                  {previewRows
                    .slice(logic.hasHeaderRow ? 1 : 0)
                    .map((row, rowIndex) => (
                      <div
                        key={rowIndex}
                        className="column-mapping-preview-item"
                      >
                        {row[index] !== undefined ? String(row[index]) : ""}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {logic.isProcessingData && (
          <div className="column-mapping-processing">
            <CircularProgress size={24} className="column-mapping-spinner" />
            <p className="column-mapping-processing-message">
              Processando dados... Isso pode levar alguns segundos para grandes
              volumes de dados.
            </p>
          </div>
        )}
      </div>

      {/* Prévia dos dados processados */}
      {logic.processedData && logic.processedData.length > 0 && (
        <div className="column-mapping-preview-section">
          <h4>
            Prévia dos dados processados ({logic.processedData.length} itens)
          </h4>
          <div className="column-mapping-preview-table">
            <div className="column-mapping-preview-header">
              <div className="preview-col">Nome</div>
              <div className="preview-col">Qtd</div>
              <div className="preview-col">Stock min</div>
              <div className="preview-col">Lote</div>
              <div className="preview-col">Validade</div>
              <div className="preview-col">Un. Medida</div>
              <div className="preview-col">Preço</div>
              <div className="preview-col">Classificação</div>
            </div>

            <div className="column-mapping-preview-body">
              {logic.processedData.slice(0, 5).map((item, index) => (
                <div className="column-mapping-preview-row" key={index}>
                  <div className="preview-col">{item.nome || "-"}</div>
                  <div className="preview-col">{item.quantidade || "0"}</div>
                  <div className="preview-col">
                    {item.quantidadeIdeal ?? "-"}
                  </div>
                  <div className="preview-col">{item.lote || "N/A"}</div>
                  <div className="preview-col">
                    {item.dataValidade
                      ? moment(item.dataValidade).format("DD/MM/YYYY")
                      : "-"}
                  </div>
                  <div className="preview-col">
                    {item.unidadeMedida || "UN"}
                  </div>
                  <div className="preview-col">
                    {item.precoCompra
                      ? `${item.precoCompra.toFixed(2)}CVE`
                      : "-"}
                  </div>
                  <div className="preview-col">
                    {item.classificacao === "MP"
                      ? "Matéria Prima"
                      : "Produto Acabado"}
                  </div>
                </div>
              ))}

              {logic.processedData.length > 5 && (
                <div className="column-mapping-preview-more">
                  + {logic.processedData.length - 5} mais itens
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Erros de validação */}
      {(logic.errors.columnErrors.length > 0 ||
        logic.errors.rowErrors.length > 0) && (
        <ImportErrorList
          columnErrors={logic.errors.columnErrors}
          rowErrors={logic.errors.rowErrors}
        />
      )}
    </div>
  );
}

export default ColumnMappingStep;
